// Vendored, zero-dependency QR Code encoder (MIT).
// Adapted from Project Nayuki's "QR Code generator" (MIT License):
//   https://www.nayuki.io/page/qr-code-generator-library
// Supports byte-mode ISO-8859-1/ASCII input, EC levels L/M/Q/H, automatic
// version selection (1-40), Reed-Solomon ECC, and lowest-penalty mask
// selection. Trimmed to the small API this project needs (see bottom).
//
// This file is fully self-contained: no imports, no network, no npm.

type Ecc = 'L' | 'M' | 'Q' | 'H';

// Ordinal of each EC level as used by the QR format-info tables (L=1, M=0, Q=3, H=2)
const ECC_FORMAT_BITS: Record<Ecc, number> = { L: 1, M: 0, Q: 3, H: 2 };
// Index into the per-version error-correction tables below.
const ECC_TABLE_INDEX: Record<Ecc, number> = { L: 0, M: 1, Q: 2, H: 3 };

const MIN_VERSION = 1;
const MAX_VERSION = 40;

// --- QR spec tables (indexed [eccIndex][version]) -------------------------

// Number of error-correction codewords per block.
const ECC_CODEWORDS_PER_BLOCK: number[][] = [
  // Version: 0(unused) 1  2  3  4  5  6  7  8  9  10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // L
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28], // M
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // Q
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // H
];

// Number of error-correction blocks.
const NUM_ERROR_CORRECTION_BLOCKS: number[][] = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25], // L
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49], // M
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68], // Q
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81], // H
];

// --- Reed-Solomon over GF(256), primitive polynomial 0x11D --------------

function reedSolomonComputeDivisor(degree: number): number[] {
  if (degree < 1 || degree > 255) throw new RangeError('Degree out of range');
  const result: number[] = [];
  for (let i = 0; i < degree - 1; i++) result.push(0);
  result.push(1); // Monic monomial x^degree
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      result[j] = reedSolomonMultiply(result[j], root);
      if (j + 1 < result.length) result[j] ^= result[j + 1];
    }
    root = reedSolomonMultiply(root, 0x02);
  }
  return result;
}

function reedSolomonComputeRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result: number[] = divisor.map(() => 0);
  for (const b of data) {
    const factor = b ^ (result.shift() as number);
    result.push(0);
    divisor.forEach((coef, i) => {
      result[i] ^= reedSolomonMultiply(coef, factor);
    });
  }
  return result;
}

function reedSolomonMultiply(x: number, y: number): number {
  if (x >>> 8 !== 0 || y >>> 8 !== 0) throw new RangeError('Byte out of range');
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
}

// --- Bit buffer -----------------------------------------------------------

function appendBits(val: number, len: number, bb: number[]): void {
  if (len < 0 || len > 31 || val >>> len !== 0) throw new RangeError('Value out of range');
  for (let i = len - 1; i >= 0; i--) bb.push((val >>> i) & 1);
}

// --- Data / codeword construction ----------------------------------------

// Bit width of the byte-mode character-count field for a given version.
function byteModeCharCountBits(version: number): number {
  if (version <= 9) return 8;
  if (version <= 26) return 16;
  return 16;
}

// Total number of usable data codewords for the version+ECL.
function getNumDataCodewords(version: number, ecl: Ecc): number {
  const i = ECC_TABLE_INDEX[ecl];
  return (
    Math.floor(getNumRawDataModules(version) / 8) -
    ECC_CODEWORDS_PER_BLOCK[i][version] * NUM_ERROR_CORRECTION_BLOCKS[i][version]
  );
}

// Number of data-bearing modules (before splitting into data + ECC codewords).
function getNumRawDataModules(version: number): number {
  if (version < MIN_VERSION || version > MAX_VERSION) throw new RangeError('Version out of range');
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) result -= 36;
  }
  return result;
}

// Encode ASCII/Latin-1 text as byte-mode segment bits for the chosen version.
function makeByteSegmentBits(bytes: readonly number[], version: number): number[] {
  const bb: number[] = [];
  appendBits(0x4, 4, bb); // Byte mode indicator
  appendBits(bytes.length, byteModeCharCountBits(version), bb);
  for (const b of bytes) appendBits(b, 8, bb);
  return bb;
}

// Convert text -> Latin-1 byte values. Throws on out-of-range characters.
function toLatin1Bytes(text: string): number[] {
  const bytes: number[] = [];
  for (const ch of text) {
    const code = ch.codePointAt(0) as number;
    if (code > 0xff) throw new RangeError(`Character out of ISO-8859-1 range: U+${code.toString(16)}`);
    bytes.push(code);
  }
  return bytes;
}

// Choose the smallest version (1..40) whose data capacity fits, then build
// the full sequence of data codewords (with padding), then add ECC.
function buildCodewords(text: string, ecl: Ecc): { version: number; codewords: number[] } {
  const bytes = toLatin1Bytes(text);
  let version = -1;
  let dataUsedBits = -1;
  for (let v = MIN_VERSION; v <= MAX_VERSION; v++) {
    const capacityBits = getNumDataCodewords(v, ecl) * 8;
    const usedBits = 4 + byteModeCharCountBits(v) + bytes.length * 8;
    if (usedBits <= capacityBits) {
      version = v;
      dataUsedBits = usedBits;
      break;
    }
  }
  if (version === -1) throw new RangeError('Data too long for QR code (max version 40)');

  const bb = makeByteSegmentBits(bytes, version);
  if (bb.length !== dataUsedBits) throw new Error('Internal bit-length mismatch');

  const dataCapacityBits = getNumDataCodewords(version, ecl) * 8;
  // Terminator (up to 4 bits)
  appendBits(0, Math.min(4, dataCapacityBits - bb.length), bb);
  // Pad to a byte boundary
  appendBits(0, (8 - (bb.length % 8)) % 8, bb);
  // Pad with alternating bytes until capacity
  for (let padByte = 0xec; bb.length < dataCapacityBits; padByte ^= 0xec ^ 0x11) {
    appendBits(padByte, 8, bb);
  }

  // Pack bits into data codewords (bytes)
  const dataCodewords: number[] = [];
  for (let i = 0; i < bb.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bb[i + j];
    dataCodewords.push(byte);
  }

  const codewords = addEccAndInterleave(dataCodewords, version, ecl);
  return { version, codewords };
}

// Reed-Solomon encode the data codewords, split into blocks, and interleave.
function addEccAndInterleave(data: readonly number[], version: number, ecl: Ecc): number[] {
  const i = ECC_TABLE_INDEX[ecl];
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[i][version];
  const blockEccLen = ECC_CODEWORDS_PER_BLOCK[i][version];
  const rawCodewords = Math.floor(getNumRawDataModules(version) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);

  const blocks: number[][] = [];
  const rsDiv = reedSolomonComputeDivisor(blockEccLen);
  for (let b = 0, k = 0; b < numBlocks; b++) {
    const datLen = shortBlockLen - blockEccLen + (b < numShortBlocks ? 0 : 1);
    const dat = data.slice(k, k + datLen);
    k += datLen;
    const ecc = reedSolomonComputeRemainder(dat, rsDiv);
    const block = dat.slice();
    if (b < numShortBlocks) block.push(0); // placeholder for interleaving alignment
    for (const e of ecc) block.push(e);
    blocks.push(block);
  }

  // Interleave the codewords column-by-column.
  const result: number[] = [];
  const maxLen = blocks[0].length; // long-block length (with padding slot)
  for (let idx = 0; idx < maxLen; idx++) {
    for (let b = 0; b < blocks.length; b++) {
      // Skip the padding slot of short blocks (the 2nd-to-last data column).
      if (idx !== shortBlockLen - blockEccLen || b >= numShortBlocks) {
        result.push(blocks[b][idx]);
      }
    }
  }
  return result;
}

// --- Matrix construction --------------------------------------------------

class QrCode {
  readonly version: number;
  readonly ecl: Ecc;
  readonly size: number;
  private readonly modules: boolean[][];
  private readonly isFunction: boolean[][];

  constructor(version: number, ecl: Ecc, codewords: readonly number[], mask: number) {
    if (version < MIN_VERSION || version > MAX_VERSION) throw new RangeError('Version out of range');
    this.version = version;
    this.ecl = ecl;
    this.size = version * 4 + 17;
    this.modules = QrCode.emptyGrid(this.size);
    this.isFunction = QrCode.emptyGrid(this.size);

    this.drawFunctionPatterns();
    this.drawCodewords(codewords);
    this.applyBestMask(mask);
  }

  getModules(): boolean[][] {
    return this.modules.map((row) => row.slice());
  }

  private static emptyGrid(size: number): boolean[][] {
    const grid: boolean[][] = [];
    for (let y = 0; y < size; y++) grid.push(new Array<boolean>(size).fill(false));
    return grid;
  }

  private setFunctionModule(x: number, y: number, isDark: boolean): void {
    this.modules[y][x] = isDark;
    this.isFunction[y][x] = true;
  }

  private drawFunctionPatterns(): void {
    // Timing patterns
    for (let i = 0; i < this.size; i++) {
      this.setFunctionModule(6, i, i % 2 === 0);
      this.setFunctionModule(i, 6, i % 2 === 0);
    }
    // Finder patterns (three corners) + their separators
    this.drawFinderPattern(3, 3);
    this.drawFinderPattern(this.size - 4, 3);
    this.drawFinderPattern(3, this.size - 4);
    // Alignment patterns
    const alignPos = this.getAlignmentPatternPositions();
    const numAlign = alignPos.length;
    for (let i = 0; i < numAlign; i++) {
      for (let j = 0; j < numAlign; j++) {
        // Skip the three finder corners
        if (!((i === 0 && j === 0) || (i === 0 && j === numAlign - 1) || (i === numAlign - 1 && j === 0))) {
          this.drawAlignmentPattern(alignPos[i], alignPos[j]);
        }
      }
    }
    // Format + version info (real data drawn after mask is chosen; reserve now)
    this.drawFormatBits(0); // placeholder, overwritten in applyBestMask
    this.drawVersionInfo();
  }

  private drawFinderPattern(cx: number, cy: number): void {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const x = cx + dx;
        const y = cy + dy;
        if (x >= 0 && x < this.size && y >= 0 && y < this.size) {
          this.setFunctionModule(x, y, dist !== 2 && dist !== 4);
        }
      }
    }
  }

  private drawAlignmentPattern(cx: number, cy: number): void {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        this.setFunctionModule(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }

  private getAlignmentPatternPositions(): number[] {
    if (this.version === 1) return [];
    const numAlign = Math.floor(this.version / 7) + 2;
    const step = this.version === 32 ? 26 : Math.ceil((this.version * 4 + 4) / (numAlign * 2 - 2)) * 2;
    const result = [6];
    for (let pos = this.size - 7; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
    return result;
  }

  private drawFormatBits(mask: number): void {
    const data = (ECC_FORMAT_BITS[this.ecl] << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((data << 10) | rem) ^ 0x5412;

    // First copy (around top-left finder)
    for (let i = 0; i <= 5; i++) this.setFunctionModule(8, i, getBit(bits, i));
    this.setFunctionModule(8, 7, getBit(bits, 6));
    this.setFunctionModule(8, 8, getBit(bits, 7));
    this.setFunctionModule(7, 8, getBit(bits, 8));
    for (let i = 9; i < 15; i++) this.setFunctionModule(14 - i, 8, getBit(bits, i));

    // Second copy (split between the other two finders)
    for (let i = 0; i < 8; i++) this.setFunctionModule(this.size - 1 - i, 8, getBit(bits, i));
    for (let i = 8; i < 15; i++) this.setFunctionModule(8, this.size - 15 + i, getBit(bits, i));
    this.setFunctionModule(8, this.size - 8, true); // Always-dark module
  }

  private drawVersionInfo(): void {
    if (this.version < 7) return;
    let rem = this.version;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (this.version << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const bit = getBit(bits, i);
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.setFunctionModule(a, b, bit);
      this.setFunctionModule(b, a, bit);
    }
  }

  private drawCodewords(codewords: readonly number[]): void {
    let i = 0; // bit index into codewords
    for (let right = this.size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5; // skip vertical timing column
      for (let vert = 0; vert < this.size; vert++) {
        for (let j = 0; j < 2; j++) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? this.size - 1 - vert : vert;
          if (!this.isFunction[y][x] && i < codewords.length * 8) {
            this.modules[y][x] = getBit(codewords[i >>> 3], 7 - (i & 7));
            i++;
          }
        }
      }
    }
  }

  private applyMask(mask: number): void {
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        let invert: boolean;
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break;
          case 1: invert = y % 2 === 0; break;
          case 2: invert = x % 3 === 0; break;
          case 3: invert = (x + y) % 3 === 0; break;
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: throw new RangeError('Mask out of range');
        }
        if (invert && !this.isFunction[y][x]) this.modules[y][x] = !this.modules[y][x];
      }
    }
  }

  // Try all 8 masks, keep the one with the lowest penalty. `preferred` < 0 means auto.
  private applyBestMask(preferred: number): void {
    let bestMask = preferred;
    if (preferred < 0) {
      let minPenalty = Infinity;
      for (let m = 0; m < 8; m++) {
        this.drawFormatBits(m);
        this.applyMask(m);
        const penalty = this.getPenaltyScore();
        if (penalty < minPenalty) {
          minPenalty = penalty;
          bestMask = m;
        }
        this.applyMask(m); // undo (XOR twice)
      }
    }
    this.drawFormatBits(bestMask);
    this.applyMask(bestMask);
  }

  private getPenaltyScore(): number {
    let result = 0;
    const size = this.size;
    const mods = this.modules;

    // Adjacent modules in rows with same color
    for (let y = 0; y < size; y++) {
      let runColor = false;
      let runX = 0;
      const runHistory = new Array<number>(7).fill(0);
      for (let x = 0; x < size; x++) {
        if (mods[y][x] === runColor) {
          runX++;
          if (runX === 5) result += 3;
          else if (runX > 5) result++;
        } else {
          this.finderPenaltyAddHistory(runX, runHistory);
          if (!runColor) result += this.finderPenaltyCountPatterns(runHistory) * 40;
          runColor = mods[y][x];
          runX = 1;
        }
      }
      result += this.finderPenaltyTerminateAndCount(runColor, runX, runHistory) * 40;
    }
    // Adjacent modules in columns with same color
    for (let x = 0; x < size; x++) {
      let runColor = false;
      let runY = 0;
      const runHistory = new Array<number>(7).fill(0);
      for (let y = 0; y < size; y++) {
        if (mods[y][x] === runColor) {
          runY++;
          if (runY === 5) result += 3;
          else if (runY > 5) result++;
        } else {
          this.finderPenaltyAddHistory(runY, runHistory);
          if (!runColor) result += this.finderPenaltyCountPatterns(runHistory) * 40;
          runColor = mods[y][x];
          runY = 1;
        }
      }
      result += this.finderPenaltyTerminateAndCount(runColor, runY, runHistory) * 40;
    }
    // 2x2 blocks of same color
    for (let y = 0; y < size - 1; y++) {
      for (let x = 0; x < size - 1; x++) {
        const c = mods[y][x];
        if (c === mods[y][x + 1] && c === mods[y + 1][x] && c === mods[y + 1][x + 1]) result += 3;
      }
    }
    // Balance of dark vs light modules
    let dark = 0;
    for (const row of mods) for (const cell of row) if (cell) dark++;
    const total = size * size;
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
    result += k * 10;
    return result;
  }

  private finderPenaltyCountPatterns(runHistory: readonly number[]): number {
    const n = runHistory[1];
    const core =
      n > 0 &&
      runHistory[2] === n &&
      runHistory[3] === n * 3 &&
      runHistory[4] === n &&
      runHistory[5] === n;
    return (
      (core && runHistory[0] >= n * 4 && runHistory[6] >= n ? 1 : 0) +
      (core && runHistory[6] >= n * 4 && runHistory[0] >= n ? 1 : 0)
    );
  }

  private finderPenaltyTerminateAndCount(
    currentRunColor: boolean,
    currentRunLength: number,
    runHistory: number[],
  ): number {
    let runLength = currentRunLength;
    if (currentRunColor) {
      this.finderPenaltyAddHistory(runLength, runHistory);
      runLength = 0;
    }
    runLength += this.size;
    this.finderPenaltyAddHistory(runLength, runHistory);
    return this.finderPenaltyCountPatterns(runHistory);
  }

  private finderPenaltyAddHistory(currentRunLength: number, runHistory: number[]): void {
    if (runHistory[0] === 0) currentRunLength += this.size; // add light border to first run
    runHistory.pop();
    runHistory.unshift(currentRunLength);
  }
}

function getBit(x: number, i: number): boolean {
  return ((x >>> i) & 1) !== 0;
}

// --- Public API -----------------------------------------------------------

/** Returns the QR as a boolean matrix; true = dark module. */
export function qrModules(text: string, ecl: Ecc = 'M'): boolean[][] {
  const { version, codewords } = buildCodewords(text, ecl);
  const qr = new QrCode(version, ecl, codewords, -1);
  return qr.getModules();
}

/**
 * Returns a complete, self-contained <svg>…</svg> string rendering the QR.
 * `size` = pixel width/height of the svg; `margin` = quiet-zone in modules.
 * Dark modules use `dark` (default currentColor); light uses `light`.
 */
export function qrToSvg(
  text: string,
  opts: { size?: number; margin?: number; ecl?: Ecc; dark?: string; light?: string } = {},
): string {
  const size = opts.size ?? 256;
  const margin = opts.margin ?? 4;
  const ecl = opts.ecl ?? 'M';
  const dark = opts.dark ?? 'currentColor';
  const light = opts.light ?? '#ffffff';

  const mods = qrModules(text, ecl);
  const count = mods.length;
  const dim = count + margin * 2;

  let path = '';
  for (let y = 0; y < count; y++) {
    for (let x = 0; x < count; x++) {
      if (mods[y][x]) path += `M${x + margin},${y + margin}h1v1h-1z`;
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" ` +
    `viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges">` +
    `<rect width="${dim}" height="${dim}" fill="${light}"/>` +
    `<path d="${path}" fill="${dark}"/>` +
    `</svg>`
  );
}
